import { useMutation } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { memo, Suspense, useEffect, useRef } from 'react'
import 'simplebar-react/dist/simplebar.min.css'

import { PharaohWrapper, ToastProvider } from '@orus.eu/pharaoh'
import { TemporaryNotificationAlertProvider } from '@orus.eu/pharaoh/src/components/callout-notification/notification/alert'

import { useTRPC } from './client.ts'
import { GlobalLoadingState } from './components/molecules/global-loading-state.tsx'
import { EmbeddingPartnerWrapper } from './components/templates/embedding-partner-wrapper.tsx'
import { OperatingZoneWrapper } from './components/templates/operating-zone-wrapper.tsx'
import { SessionDefaultLanguageWrapper } from './components/templates/session-default-language-wrapper.tsx'
import { SessionLanguageProvider } from './components/templates/session-language-provider.tsx'
import { SessionProvider } from './components/templates/session-provider.tsx'
import { logger } from './lib/logger.ts'
import { initializePostHogEventRenaming } from './lib/posthog-utils.ts'
import { useSessionManager } from './lib/session.ts'
import { sendButtonClickMessage, sendTabViewMessage } from './lib/tracking/pharaoh-tracking-callbacks.ts'
import { useStartTrackingClicksOnTrustpilotWidget } from './lib/tracking/start-tracking-clicks-on-trustpilot-widget.ts'
import { router } from './router.tsx'
import { UpdateHandler } from './update.tsx'

export const App = memo(function App() {
  const trpc = useTRPC()
  const sessionManager = useSessionManager()
  const { mutate: sendEvent } = useMutation(trpc.log.sendEvent.mutationOptions())
  const loggerTransportInitializedRef = useRef(false)

  useEffect(() => {
    if (loggerTransportInitializedRef.current) return
    loggerTransportInitializedRef.current = true

    void sessionManager.getSessionResult().then(() => {
      logger.addTransport({
        event(event, metadata) {
          sendEvent({ event, metadata })
        },
      })
    })
  }, [sessionManager, sendEvent])

  // Initialize PostHog event renaming for activity search events
  useEffect(() => {
    initializePostHogEventRenaming()
  }, [])

  // Track Trustpilot widget clicks
  useStartTrackingClicksOnTrustpilotWidget()

  return (
    <PharaohWrapper clickButtonCallback={sendButtonClickMessage} viewTabCallback={sendTabViewMessage}>
      <TemporaryNotificationAlertProvider>
        <ToastProvider>
          <SessionProvider>
            <SessionDefaultLanguageWrapper>
              <SessionLanguageProvider>
                <OperatingZoneWrapper>
                  <EmbeddingPartnerWrapper>
                    <Suspense fallback={<GlobalLoadingState />}>
                      {/* Catches expected failures stored in the global state and display a fallback screen */}
                      <UpdateHandler>
                        <RouterProvider router={router} />
                      </UpdateHandler>
                    </Suspense>
                  </EmbeddingPartnerWrapper>
                </OperatingZoneWrapper>
              </SessionLanguageProvider>
            </SessionDefaultLanguageWrapper>
          </SessionProvider>
        </ToastProvider>
      </TemporaryNotificationAlertProvider>
    </PharaohWrapper>
  )
})
